import React from "react";
import {
    Box,
    Typography,
    Chip,
    IconButton,
    Button,
    LinearProgress,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Divider,
    Tooltip,
    Link as MUILink,
    makeStyles
} from "@material-ui/core";
import {
    Info,
    FileCopy as Copy,
    Link as Link2,
    Schedule as Clock,
    CheckCircle,
    RadioButtonUnchecked as Circle,
    OpenInNew as ExternalLink,
    Close as X,
    LocalOffer as Tag
} from "@material-ui/icons";
import DialogComponent from "../../components/Dialog";
import { OwnerMissing } from "./alerts/ownerMissing";
import { DefaultSettingsMissing } from "./alerts/defaultSettingsMissing";
import { StartRentMissing } from "./alerts/startRentMissing";
import { IndexationAlert } from "./alerts/indexationAlert";
import { TemplateMissing } from "./alerts/templateMissing";

const useStyles = makeStyles((theme) => ({
    metaRow: {
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(1),
        marginBottom: theme.spacing(1),
        [theme.breakpoints.up("sm")]: {
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between"
        }
    },
    metaLeft: {
        display: "flex",
        flexDirection: "row",
        gap: theme.spacing(1),
        alignItems: "center"
    },
    idSection: {
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        gap: theme.spacing(0.5)
    },
    timeSection: {
        display: "flex",
        flexDirection: "row",
        gap: theme.spacing(0.5),
        alignItems: "center"
    },
    message: {
        marginBottom: theme.spacing(2)
    },
    progressBox: {
        marginBottom: theme.spacing(2)
    },
    progressHeader: {
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        marginBottom: theme.spacing(0.5)
    },
    progressBar: {
        height: 10,
        borderRadius: 999,
        backgroundColor: theme.palette.action.hover
    },
    progressBarComplete: {
        "& .MuiLinearProgress-bar": {
            borderRadius: 999,
            backgroundColor: theme.palette.success.main
        }
    },
    progressBarIncomplete: {
        "& .MuiLinearProgress-bar": {
            borderRadius: 999,
            backgroundColor: theme.palette.error.main
        }
    },
    stepsTitle: {
        textTransform: "uppercase",
        letterSpacing: 0.5,
        color: theme.palette.text.secondary,
        marginBottom: theme.spacing(1)
    },
    noSteps: {
        color: theme.palette.text.secondary,
        marginBottom: theme.spacing(2)
    },
    listItemIcon: {
        minWidth: 28
    },
    listItemIconDone: {
        color: theme.palette.success.main
    },
    listItemIconNotDone: {
        color: theme.palette.text.disabled
    },
    stepText: {
        textDecoration: "none"
    },
    stepTextDone: {
        textDecoration: "line-through"
    },
    secondaryAction: {
        display: "flex",
        flexDirection: "row",
        gap: theme.spacing(1)
    },
    tagsSection: {
        marginTop: theme.spacing(1)
    },
    tagsDivider: {
        marginBottom: theme.spacing(1)
    },
    tagsContainer: {
        display: "flex",
        flexDirection: "row",
        gap: theme.spacing(1),
        flexWrap: "wrap"
    },
    actions: {
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(1),
        width: "100%",
        [theme.breakpoints.up("sm")]: {
            flexDirection: "row"
        }
    },
    actionSpacer: {
        flex: 1
    }
}));

/**
 * AlertDetail
 * A modern dialog to show alert meta, description, and actionable steps.
 *
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *  - alert: {
 *      id?: string,
 *      title: string,
 *      message?: string,
 *      category?: string,
 *      severity?: "critical" | "high" | "medium" | "low" | "info",
 *      source?: string,
 *      createdAt?: string | number | Date,
 *      tags?: string[],
 *      actionUrl?: string,
 *      steps?: Array<{ id?: string|number, label: string, done?: boolean, link?: string }>
 *    }
 *  - onGoTo?: (alert: any) => void          // fallback when no actionUrl
 *  - onResolveStep?: (stepIndex: number, step: any) => void
 *  - onResolveAll?: (alert: any) => void
 *  - onSnooze?: (alert: any, minutes?: number) => void
 */
export default function AlertDetail({
    open,
    onClose,
    alert = {},
    onGoTo,
    onResolveStep,
    onResolveAll,
    onSnooze
}) {
    const classes = useStyles();

    const {
        id,
        title,
        message,
        category,
        severity,
        createdAt,
        tags = [],
        actionUrl,
        steps = []
    } = alert || {
        title: "Alert",
        message: "",
        severity: "medium",
    };

    const doneCount = steps.filter(s => !!s.done).length;
    const total = steps.length || 0;
    const completion = total ? Math.round((doneCount / total) * 100) : 0;
    const isComplete = completion === 100;

    const severityMap = {
        critical: { label: "Critical", color: "secondary" },
        high: { label: "High", color: "secondary" },
        medium: { label: "Medium", color: "default" },
        low: { label: "Low", color: "primary" },
        info: { label: "Info", color: "primary" }
    };
    const sev = severityMap[severity] || severityMap.medium;

    const created = createdAt ? new Date(createdAt) : null;

    const copyText = async (text) => {
        try {
            await navigator.clipboard.writeText(text || "");
        } catch { }
    };

    return (
        <DialogComponent open={open} onClose={onClose} fullWidth maxWidth="md" title="Alert Details">
           <Box p={2}>
            {/* Description */}
            {message && (
                <Typography variant="body1" className={classes.message}>
                    {message}
                </Typography>
            )}

            {/* Steps */}
            <Typography variant="subtitle2" className={classes.stepsTitle}>
                Steps to resolve
            </Typography>
            {Steps(alert)}
               </Box>
        </DialogComponent>
    );
}

const Steps = (alert) => {
    if (alert?.message?.includes("No default system settings"))
        return <DefaultSettingsMissing />
    else if (alert?.message?.includes("Logo is missing"))
        return <DefaultSettingsMissing />
    else if (alert?.message?.includes("template is missing"))
        return <TemplateMissing metaData={alert?.metaData} />
    else if (alert?.message?.includes("Owner information missing"))
        return <OwnerMissing metaData={alert?.metaData} />
    else if (alert?.message?.includes("Start rent cannot be 0"))
        return <StartRentMissing metaData={alert?.metaData}/>
    else if (alert?.message?.includes("Govt had not published"))
        return <IndexationAlert/>
    else if (alert?.message?.includes("no contract with anniversary"))
        return <IndexationAlert/>
        else
            <Typography>No Steps</Typography>
}
